// The detail panel of one connection: its name, type and slug with the
// enable/delete/close actions, its settings, its health, one section per
// capability and its activity. Credentials are write-only — the API never
// returns them, so only a "credentials stored" note is shown. Every string
// comes from the adminConnectors namespace.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { apiFetchJson } from "../apiClient";
import usePagedList from "../hooks/usePagedList";
import { summarizeHealth } from "../utils/connectionForm";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import {
  AccessCard, DetailSection, HEALTH_BADGE, NeutralCard, SourceCard, formatWhen, useCardRequest,
} from "./CapabilityCards";
import "./AdminCollections.css";
import "./AdminConnectors.css";

/** The panel header: name, type, slug and workspace, plus enable/disable,
 *  delete (with an inline confirmation) and close. */
function DetailHeader({ connection, kind, onChanged, onClose, onDeleted }) {
  const { t } = useTranslation();
  const { busy, error, run } = useCardRequest();
  const [confirming, setConfirming] = useState(false);

  const toggleEnabled = () => run(() => apiFetchJson(`/api/v1/connections/${connection.id}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled: !connection.enabled }),
  }), onChanged);

  const remove = () => run(
    () => apiFetchJson(`/api/v1/connections/${connection.id}`, { method: "DELETE" }),
    () => { setConfirming(false); if (typeof onDeleted === "function") onDeleted(); },
  );

  return (
    <header className="cx-detail-head">
      <div className="cx-detail-titles">
        <h3 className="ds-section-title ds-flush">{connection.name}</h3>
        <div className="cx-detail-meta">
          <span>{kind?.label || connection.kind}</span>
          <span className="ds-mono">{connection.slug}</span>
          {connection.database && <span>{connection.database}</span>}
          {!connection.enabled && <span className="ds-badge">{t("adminConnectors.disabledBadge")}</span>}
        </div>
      </div>
      <div className="cx-detail-actions">
        {confirming ? (
          <>
            <span className="ac-confirm-label">{t("adminConnectors.deletePrompt")}</span>
            <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy} onClick={remove}>
              {t("adminConnectors.confirmDelete")}
            </button>
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" disabled={busy}
                    onClick={() => setConfirming(false)}>
              {t("adminConnectors.cancel")}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy} onClick={toggleEnabled}>
              {connection.enabled ? t("adminConnectors.disable") : t("adminConnectors.enable")}
            </button>
            <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy}
                    onClick={() => setConfirming(true)}>
              {t("adminConnectors.delete")}
            </button>
          </>
        )}
        <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm cx-icon-btn"
                aria-label={t("adminConnectors.close")} title={t("adminConnectors.close")}
                onClick={() => { if (typeof onClose === "function") onClose(); }}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {error && <div className="ds-alert ds-alert--danger ds-flush cx-detail-error" role="alert">{error}</div>}
    </header>
  );
}

/** The slug agents address the connection by, and whether credentials are stored. */
function SettingsSection({ connection, onChanged }) {
  const { t } = useTranslation();
  const { busy, error, run } = useCardRequest();
  const [slug, setSlug] = useState(connection.slug || "");

  useEffect(() => { setSlug(connection.slug || ""); }, [connection.slug]);

  const save = () => {
    const trimmed = slug.trim();
    if (!trimmed) return;                 // a blank slug is never sent
    run(() => apiFetchJson(`/api/v1/connections/${connection.id}`, {
      method: "PATCH",
      body: JSON.stringify({ slug: trimmed }),
    }), onChanged);
  };

  return (
    <DetailSection title={t("adminConnectors.settingsTitle")}>
      <div className="ds-field">
        <label className="ds-label" htmlFor="cd-slug">{t("adminConnectors.editSlug")}</label>
        <div className="cx-inline">
          <input id="cd-slug" className="ds-input ds-mono" value={slug} disabled={busy}
                 onChange={(e) => setSlug(e.target.value)} />
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                  disabled={busy || slug.trim() === (connection.slug || "")} onClick={save}>
            {t("adminConnectors.slugSave")}
          </button>
        </div>
        <span className="ds-help">{t("adminConnectors.slugHint")}</span>
      </div>
      {connection.has_secrets && (
        <div><span className="ds-badge ds-badge--success">{t("adminConnectors.credentialsStored")}</span></div>
      )}
      {error && <div className="ds-alert ds-alert--danger ds-flush" role="alert">{error}</div>}
    </DetailSection>
  );
}

/** The health badge, the last test and the "Test connection" button. */
function HealthSection({ connection, onChanged }) {
  const { t } = useTranslation();
  const { busy, error, run } = useCardRequest();
  const [result, setResult] = useState(null);
  const shown = result || connection.last_health;
  const health = summarizeHealth(shown, t);
  const detail = (shown && shown.detail) || "";
  const testedAt = formatWhen(connection.last_health_at) || t("adminConnectors.never");

  return (
    <DetailSection
      title={t("adminConnectors.healthTitle")}
      actions={(
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                onClick={() => run(
                  () => apiFetchJson(`/api/v1/connections/${connection.id}/test`, { method: "POST" }),
                  (data) => { setResult(data); onChanged(); })}>
          {t("adminConnectors.testConnection")}
        </button>
      )}
    >
      <div className="cx-status-line">
        <span className={HEALTH_BADGE[health.tone] || HEALTH_BADGE.neutral}>{health.label}</span>
        <span className="ds-meta">{t("adminConnectors.lastTested", { when: testedAt })}</span>
      </div>
      {detail && <p className="cx-note">{detail}</p>}
      {error && <div className="ds-alert ds-alert--danger ds-flush" role="alert">{error}</div>}
    </DetailSection>
  );
}

/** An argument value as text: strings as they are, everything else as JSON. */
function valueText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** The redacted arguments of an event as `key=value` pairs. The API redacts
 *  secret values BEFORE it stores them, so `[redacted]` is a marker and not a
 *  secret: it is shown as it is. A missing or malformed argument object
 *  simply yields no pairs. */
function argsOf(event) {
  const args = event && event.redacted_args;
  if (!args || typeof args !== "object" || Array.isArray(args)) return [];
  return Object.entries(args).map(([key, value]) => [key, valueText(value)]);
}

/** The receipt of a delivery: the API answers it as an object, a JSON string
 *  is parsed defensively. */
function receiptOf(delivery) {
  const receipt = delivery && delivery.receipt;
  if (!receipt) return null;
  if (typeof receipt === "string") {
    try {
      return JSON.parse(receipt);
    } catch {
      return null;
    }
  }
  return typeof receipt === "object" ? receipt : null;
}

/** The record count of a delivery. i18next picks the plural form of the
 *  active language (Intl.PluralRules), so Arabic gets its six forms. */
const recordsLabel = (count, t) => t("adminConnectors.recordsCount", { count });

/** Newest first: the API answers in that order already, but the pages that
 *  "Load more" appends are sorted together with the first one. */
function newestFirst(items) {
  const timeOf = (item) => {
    const value = item && item.created_at;
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  };
  const list = Array.isArray(items) ? items : [];
  if (list.filter((item) => timeOf(item) !== null).length < 2) return list;
  return [...list].sort((a, b) => {
    const ta = timeOf(a);
    const tb = timeOf(b);
    if (ta === null) return tb === null ? 0 : 1;      // undated rows last
    if (tb === null) return -1;
    return tb - ta;
  });
}

/** One cursor-paged list: its table, its empty state, its error alert and its
 *  "Load more" button. `renderRow` returns the cells of one row. */
function PagedList({ items, loading, error, hasMore, loadMore, columns, renderRow, emptyLabel, testId }) {
  const { t } = useTranslation();
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="ac-detail-section" data-testid={testId}>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      {list.length > 0 ? (
        <div className="ds-table-wrap">
          <table className="ds-table">
            <thead>
              <tr>{columns.map((label) => <th key={label}>{label}</th>)}</tr>
            </thead>
            <tbody>{list.map((item, index) => renderRow(item, index))}</tbody>
          </table>
        </div>
      ) : error ? null : loading ? (
        <div className="ds-empty">{t("adminConnectors.loading")}</div>
      ) : (
        <div className="ds-empty">{emptyLabel}</div>
      )}
      {hasMore && (
        <button type="button" className="ds-btn ds-btn--secondary ac-load-more" disabled={loading}
                onClick={() => loadMore()}>
          {t("adminConnectors.loadMore")}
        </button>
      )}
    </div>
  );
}

/** The activity of one connection: the events of the calls made through it
 *  and — for a sink connection — its deliveries.
 *
 *  The events are only fetched once the user asks for them: the events path
 *  stays `null` while the card is collapsed, so the endpoint is never called
 *  for a list nobody looks at. The deliveries belong to the connection itself
 *  and are read as soon as it has the `sink` capability. */
function ActivityCard({ connection }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const capabilities = Array.isArray(connection.capabilities) ? connection.capabilities : [];
  const isSink = capabilities.includes("sink");
  const events = usePagedList(expanded ? `/api/v1/connections/${connection.id}/events` : null);
  const deliveries = usePagedList(isSink ? `/api/v1/connections/${connection.id}/deliveries` : null);

  const eventRow = (event, index) => {
    const id = (event && event.id) || `event-${index}`;
    const outcome = (event && event.outcome) || "";
    const error = (event && event.error) || "";
    const args = argsOf(event);
    return (
      <tr key={id} data-testid={`event-row-${id}`}>
        <td>{formatWhen(event && event.created_at)}</td>
        <td>{(event && event.capability) || ""}</td>
        <td className="ds-mono">{(event && event.action) || ""}</td>
        <td data-testid={`event-outcome-${id}`}>
          {outcome ? <span className={statusBadgeClass(outcome)}>{statusLabel(outcome)}</span> : null}
          {error ? <div className="ac-cell-error">{error}</div> : null}
        </td>
        <td className="ds-mono">{(event && event.run_id) || ""}</td>
        <td className="ds-mono" data-testid={`event-args-${id}`}>
          {args.map(([key, value], argIndex) => (
            <span key={`${key}-${argIndex}`}>{argIndex > 0 ? " " : ""}{key}={value}</span>
          ))}
        </td>
      </tr>
    );
  };

  const deliveryRow = (delivery, index) => {
    const id = (delivery && delivery.id) || `delivery-${index}`;
    const status = (delivery && delivery.status) || "";
    const error = (delivery && delivery.error) || "";
    const attempts = delivery && delivery.attempts;
    const count = delivery && delivery.record_count;
    const receipt = receiptOf(delivery);
    const externalId = receipt && receipt.external_id !== undefined && receipt.external_id !== null
      ? String(receipt.external_id)
      : "";
    return (
      <tr key={id} data-testid={`delivery-row-${id}`}>
        <td>{formatWhen(delivery && delivery.created_at)}</td>
        <td className="ds-mono" data-testid={`delivery-key-${id}`}>
          {(delivery && delivery.idempotency_key) || ""}
        </td>
        <td data-testid={`delivery-status-${id}`}>
          {status ? <span className={statusBadgeClass(status)}>{statusLabel(status)}</span> : null}
          {error ? <div className="ac-cell-error">{error}</div> : null}
        </td>
        <td data-testid={`delivery-attempts-${id}`}>
          {attempts === undefined || attempts === null ? "" : String(attempts)}
        </td>
        <td data-testid={`delivery-records-${id}`}>
          {count === undefined || count === null || count === "" ? "" : recordsLabel(Number(count), t)}
        </td>
        <td className="ds-mono" data-testid={`delivery-receipt-${id}`}>{externalId}</td>
      </tr>
    );
  };

  return (
    <DetailSection
      title={t("adminConnectors.activityTitle")}
      actions={(
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" aria-expanded={expanded}
                onClick={() => setExpanded((prev) => !prev)}>
          {t("adminConnectors.eventsTitle")}
          <ChevronDown size={14} aria-hidden="true" className={`cx-chevron${expanded ? " is-open" : ""}`} />
        </button>
      )}
    >

      {expanded && (
        <PagedList
          testId="activity-events"
          items={newestFirst(events.items)}
          loading={events.loading}
          error={events.error}
          hasMore={events.hasMore}
          loadMore={events.loadMore}
          emptyLabel={t("adminConnectors.eventsEmpty")}
          columns={[
            t("adminConnectors.colTime"),
            t("adminConnectors.colCapability"),
            t("adminConnectors.colAction"),
            t("adminConnectors.colOutcome"),
            t("adminConnectors.colRunId"),
            t("adminConnectors.colArgs"),
          ]}
          renderRow={eventRow}
        />
      )}

      {isSink && (
        <>
          <h4 className="ac-detail-heading">{t("adminConnectors.deliveriesTitle")}</h4>
          <PagedList
            testId="activity-deliveries"
            items={newestFirst(deliveries.items)}
            loading={deliveries.loading}
            error={deliveries.error}
            hasMore={deliveries.hasMore}
            loadMore={deliveries.loadMore}
            emptyLabel={t("adminConnectors.deliveriesEmpty")}
            columns={[
              t("adminConnectors.colTime"),
              t("adminConnectors.colKey"),
              t("adminConnectors.colStatus"),
              t("adminConnectors.colAttempts"),
              t("adminConnectors.colRecords"),
              t("adminConnectors.colReceipt"),
            ]}
            renderRow={deliveryRow}
          />
        </>
      )}
    </DetailSection>
  );
}

/**
 * The detail panel of one connection.
 *
 * @param {object} connection the connection row (id, name, slug, kind,
 *   enabled, capabilities, grants, status, last_sync_at, last_error,
 *   last_summary, schedule_seconds, last_health, last_health_at, has_secrets)
 * @param {object} kind the kind descriptor from /connection-kinds, optional
 * @param {string[]} roles the assignable role names
 * @param {Function} onChanged called after every successful mutation
 * @param {Function} onClose closes the panel
 * @param {Function} onDeleted called after the connection was deleted
 */
export default function ConnectionDetail({ connection, kind, roles, onChanged, onClose, onDeleted }) {
  const { t } = useTranslation();
  const conn = connection || {};
  const capabilities = Array.isArray(conn.capabilities) ? conn.capabilities : [];
  const notify = () => { if (typeof onChanged === "function") onChanged(); };

  return (
    <article className="ds-card cx-detail" aria-label={conn.name || t("adminConnectors.title")}>
      <DetailHeader connection={conn} kind={kind} onChanged={notify} onClose={onClose} onDeleted={onDeleted} />
      <SettingsSection connection={conn} onChanged={notify} />
      <HealthSection connection={conn} onChanged={notify} />
      {capabilities.map((capability, index) => {
        if (capability === "source") {
          return (
            <SourceCard key={`source-${index}`} capability={capability} label={t("adminConnectors.capSource")}
                        connection={conn} onChanged={notify} />
          );
        }
        if (capability === "tool") {
          return (
            <AccessCard key={`tool-${index}`} capability={capability} label={t("adminConnectors.capTool")}
                        connection={conn} kind={kind} roles={roles} onChanged={notify} showActions />
          );
        }
        if (capability === "sink") {
          return (
            <AccessCard key={`sink-${index}`} capability={capability} label={t("adminConnectors.capSink")}
                        connection={conn} kind={kind} roles={roles} onChanged={notify} />
          );
        }
        return <NeutralCard key={`${capability}-${index}`} capability={capability} />;
      })}
      <ActivityCard connection={conn} />
    </article>
  );
}
